import { Effect, References } from "effect";
import { canonicalJsonDigest } from "../../canonical-json/digest.ts";
import { canonicalJsonCause } from "../../canonical-json/cause.ts";
import { FindingDispositionError, findingDispositionDependencyError, type FindingDispositionArtifact,
  type FindingDispositionInput, type FindingDispositionSnapshotInput, type FindingAuthorityGrantInput } from "./model.ts";
import { dispositionIdentity, prepareDispositionVerification, completeDispositionVerification, prepareDispositionAuthority,
  completeDispositionAuthority, prepareDispositionRecord, completeDispositionRecord } from "./pure.ts";
import type { DurableFindingRecord } from "../../development-coordinator-model.ts";
import type { FindingDispositionRow } from "../../development-coordinator-storage-model.ts";
export { encodedFindingDispositionReason } from "./pure.ts";

const owned = <A, E, R>(operation: Effect.Effect<A, E, R>) => operation.pipe(
  Effect.catchDefect(cause => Effect.fail(findingDispositionDependencyError(cause))),
  Effect.uninterruptible,
  Effect.provideService(References.PreventSchedulerYield, true)
);

/** Selection, one accepted hash and its immutable output share the caller's
 * Digest graph. Cancellation cannot abandon an accepted identity check. */
// Construct the creator's named operation at invocation. Native production
// consumers only verify/recheck/read; importing them must not retain the entire
// unused creator and its validation matrix in the Worker bundle.
export const createFindingDisposition = (input: FindingDispositionInput) => Effect.fn("FindingDisposition.create")(() => owned(
  Effect.gen(function* () {
    const identity = yield* Effect.fromResult(dispositionIdentity(input));
    const digest = yield* canonicalJsonDigest(identity).pipe(Effect.mapError(error => findingDispositionDependencyError(canonicalJsonCause(error))));
    const artifactDigest = `sha256:${digest}`;
    return Object.freeze({ ...identity, dispositionId: `disposition:v1:${artifactDigest}`, artifactDigest });
  })
))();

/** Native verification deliberately has one public refusal for malformed data
 * and failed hashing. Keep that boundary without inspecting private causes. */
export const verifyFindingDisposition = Effect.fn("FindingDisposition.verify")((artifact: unknown) =>
  Effect.gen(function* () {
    const prepared = yield* Effect.fromResult(prepareDispositionVerification(artifact));
    const digest = yield* canonicalJsonDigest(prepared.withoutDigest);
    yield* Effect.fromResult(completeDispositionVerification(prepared, digest));
  }).pipe(
    Effect.catch(tamperedArtifact),
    Effect.catchDefect(tamperedArtifact),
    Effect.uninterruptible,
    Effect.provideService(References.PreventSchedulerYield, true)
  )
);
const tamperedArtifact = () => Effect.fail(new FindingDispositionError("tampered-artifact", "Finding disposition failed immutable identity verification."));

/** Grants and finding state are snapshotted before hashing. The same check is
 * used immediately before the existing coordinator's durable mutation. */
export const recheckFindingDispositionAuthority = Effect.fn("FindingDisposition.recheckAuthority")((
  artifact: FindingDispositionArtifact, finding: FindingDispositionSnapshotInput,
  grants: readonly FindingAuthorityGrantInput[], assignmentVerified = false
) => owned(Effect.gen(function* () {
  const prepared = yield* Effect.fromResult(prepareDispositionAuthority(artifact, finding, grants));
  yield* verifyFindingDisposition(prepared.sealedArtifact);
  yield* Effect.fromResult(completeDispositionAuthority(prepared, assignmentVerified));
})));

export const findingDispositionRecord = Effect.fn("FindingDisposition.record")((row: FindingDispositionRow, finding: DurableFindingRecord) => owned(
  Effect.gen(function* () {
    const prepared = yield* Effect.fromResult(prepareDispositionRecord(row, finding));
    yield* verifyFindingDisposition(prepared.input);
    return yield* Effect.fromResult(completeDispositionRecord(prepared));
  })
));
